import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Hook for creating a timer that counts up to maxSeconds and then calls onComplete
 * This implementation is resistant to browser tab throttling when inactive
 * @param maxSeconds Maximum time in seconds, or false for infinite timer
 * @param onComplete Callback that runs when timer reaches maxSeconds
 * @param intervalDuration How often the timer should update in milliseconds
 * @param tag Optional identifier for the timer (for debugging)
 */
export function useTimer(maxSeconds: number | false, onComplete?: () => void, intervalDuration = 10, tag?: string) {
  const [seconds, setSeconds] = useState(0);
  const [done, setDone] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  // Use refs for values needed inside effects that we don't want to trigger re-renders
  const startTimeRef = useRef<number>(0);
  const lastUpdateTimeRef = useRef<number>(0);
  const animationFrameRef = useRef<number | null>(null);
  const secondsRef = useRef<number>(0);

  // Clear the existing animation frame to prevent memory leaks
  const clearTimerAnimation = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  // Stop timer function with useCallback for stable reference
  const stopTimer = useCallback(() => {
    clearTimerAnimation();
    setIsRunning(false);
  }, [clearTimerAnimation]);

  // Reset and start timer with useCallback for stable reference
  const resetAndStartTimer = useCallback(() => {
    clearTimerAnimation();
    const now = Date.now();
    startTimeRef.current = now;
    lastUpdateTimeRef.current = now;
    secondsRef.current = 0;
    setSeconds(0);
    setDone(false);
    setIsRunning(true);
  }, [clearTimerAnimation]);

  // Effect to handle onComplete when done changes
  useEffect(() => {
    if (done && onComplete) {
      onComplete();
    }
  }, [done, onComplete]);

  // Main timer effect using requestAnimationFrame
  useEffect(() => {
    if (!isRunning) return;

    // Set initial start time when starting
    if (startTimeRef.current === 0) {
      const now = Date.now();
      startTimeRef.current = now;
      lastUpdateTimeRef.current = now;
    }

    const updateTimer = () => {
      const now = Date.now();
      const deltaTime = now - lastUpdateTimeRef.current;

      // Only update if enough time has passed (based on intervalDuration)
      if (deltaTime >= intervalDuration) {
        // Calculate time passed in seconds based on actual elapsed time
        const elapsedSeconds = deltaTime / 1000;
        secondsRef.current += elapsedSeconds;
        setSeconds(secondsRef.current);

        lastUpdateTimeRef.current = now;

        // For infinite timers, call onComplete on every update if provided
        if (maxSeconds === false && onComplete) {
          onComplete();
        }

        // Check if we've reached maxSeconds
        if (maxSeconds !== false && secondsRef.current >= maxSeconds) {
          stopTimer();
          setDone(true);
          return;
        }
      }

      // Continue the animation loop
      animationFrameRef.current = requestAnimationFrame(updateTimer);
    };

    // Start the animation loop
    animationFrameRef.current = requestAnimationFrame(updateTimer);

    // Clean up on unmount or when dependencies change
    return clearTimerAnimation;
  }, [isRunning, maxSeconds, onComplete, intervalDuration, clearTimerAnimation, stopTimer]);

  return { seconds, resetAndStartTimer, stopTimer };
}
